import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import {
  MMLElement,
  MMLElementType,
  ValidationResult,
  CommonAttributes,
  CubeAttributes,
  SphereAttributes,
  ModelAttributes,
  GroupAttributes,
  AudioAttributes,
  LabelAttributes,
  ImageAttributes,
  VideoAttributes,
} from './types';
import { validateMML } from './validator';

const baseURL2022 = 'https://d23hwblhugcea5.cloudfront.net/';

const gltfLoader = new GLTFLoader();

const dracoLoader = new DRACOLoader();

dracoLoader.setDecoderPath(`${baseURL2022}Files/TEST-THREE-0.151.0/draco/`);
dracoLoader.setDecoderConfig({ type: 'js' });
gltfLoader.setDRACOLoader(dracoLoader);

export function convertMMLToThreeJS(mmlCode: string): {
  object: THREE.Object3D;
  validationResult: ValidationResult;
} {
  const validationResult = validateMML(mmlCode);

  const container = new THREE.Group();

  if (!validationResult.isValid || !validationResult.parsedElements) {
    return { object: container, validationResult };
  }

  for (const element of validationResult.parsedElements) {
    const object = convertElementToThreeJS(element);
    if (object) {
      container.add(object);
    }
  }

  return { object: container, validationResult };
}

function convertElementToThreeJS(element: MMLElement): THREE.Object3D | null {
  let object: THREE.Object3D | null = null;

  switch (element.type) {
    case MMLElementType.CUBE:
      object = createCube(element);
      break;
    case MMLElementType.SPHERE:
      object = createSphere(element);
      break;
    case MMLElementType.MODEL:
      object = createModel(element);
      break;
    case MMLElementType.GROUP:
      object = createGroup(element);
      break;
    case MMLElementType.AUDIO:
      object = createAudio(element);
      break;
    case MMLElementType.LABEL:
      object = createLabel(element);
      break;
    case MMLElementType.IMAGE:
      object = createImage(element);
      break;
    case MMLElementType.VIDEO:
      object = createVideo(element);
      break;
    default:
      console.warn(`Unsupported element type: ${element.type}`);
      return null;
  }

  if (!object) return null;

  applyCommonAttributes(object, element);

  if (element.children && element.children.length > 0) {
    for (const child of element.children) {
      const childObject = convertElementToThreeJS(child);
      if (childObject) {
        object.add(childObject);
      }
    }
  }

  return object;
}

function applyCommonAttributes(
  object: THREE.Object3D,
  element: MMLElement
): void {
  const attrs = element.attributes as unknown as CommonAttributes;

  if (attrs.x !== undefined) object.position.x = attrs.x;
  if (attrs.y !== undefined) object.position.y = attrs.y;
  if (attrs.z !== undefined) object.position.z = attrs.z;

  if (attrs.rx !== undefined)
    object.rotation.x = THREE.MathUtils.degToRad(attrs.rx);
  if (attrs.ry !== undefined)
    object.rotation.y = THREE.MathUtils.degToRad(attrs.ry);
  if (attrs.rz !== undefined)
    object.rotation.z = THREE.MathUtils.degToRad(attrs.rz);

  if (attrs.sx !== undefined) object.scale.x = attrs.sx;
  if (attrs.sy !== undefined) object.scale.y = attrs.sy;
  if (attrs.sz !== undefined) object.scale.z = attrs.sz;

  if (attrs.id !== undefined) object.name = attrs.id;
}

function createPlaceholder(element: MMLElement): THREE.Mesh {
  const attrs = element.attributes as unknown as CommonAttributes;

  const color = attrs.color || 'gray';
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(color),
  });
  const geometry = new THREE.BoxGeometry(1, 1, 1);
  const mesh = new THREE.Mesh(geometry, material);

  return mesh;
}

function createCube(element: MMLElement): THREE.Mesh {
  const attrs = element.attributes as unknown as CubeAttributes;
  const color = attrs.color || 'red';

  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
  });
  const geometry = new THREE.BoxGeometry(1, 1, 1);
  const mesh = new THREE.Mesh(geometry, material);

  mesh.castShadow = true;
  mesh.receiveShadow = true;

  return mesh;
}

function createSphere(element: MMLElement): THREE.Mesh {
  const attrs = element.attributes as unknown as SphereAttributes;
  const color = attrs.color || 'red';

  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
  });
  const geometry = new THREE.SphereGeometry(0.5, 32, 32);
  const mesh = new THREE.Mesh(geometry, material);

  mesh.castShadow = true;
  mesh.receiveShadow = true;

  return mesh;
}

function createModel(element: MMLElement): THREE.Group {
  const attrs = element.attributes as unknown as ModelAttributes;

  const group = new THREE.Group();

  if (!attrs.src) {
    group.add(createPlaceholder(element));
    return group;
  }

  const isGLTF =
    attrs.src.toLowerCase().endsWith('.glb') ||
    attrs.src.toLowerCase().endsWith('.gltf');

  if (!isGLTF) {
    group.add(createPlaceholder(element));
    return group;
  }

  gltfLoader.load(
    attrs.src,
    (gltf) => {
      const model = gltf.scene;
      const maxAnisotropy = 8;

      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (attrs.color && child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => {
                mat.color.set(attrs.color as string);
              });
            } else {
              child.material.color.set(attrs.color as string);
            }
          }

          child.castShadow = true;
          child.receiveShadow = true;

          if (child.material) {
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];

            materials.forEach((material) => {
              material.depthWrite = !material.transparent;

              material.skinning = true;

              if (material.map) {
                material.map.minFilter = THREE.LinearMipmapLinearFilter;
                material.map.magFilter = THREE.LinearFilter;
                material.map.anisotropy = maxAnisotropy;
                /* material.map.encoding = THREE.sRGBEncoding; */
              }

              if (material.normalMap) {
                material.normalMap.anisotropy = maxAnisotropy;
                /* material.normalMap.encoding = THREE.LinearEncoding; */
              }

              if (material.emissiveMap) {
                material.emissiveMap.anisotropy = maxAnisotropy;
                /* material.emissiveMap.encoding = THREE.sRGBEncoding; */
              }

              if (material.map || material.emissiveMap || material.normalMap) {
                material.needsUpdate = true;
              }

              material.envMapIntensity = 0.25;
            });
          }
        }
      });

      group.add(model);
    },
    (xhr) => {},
    (error) => {
      console.error('Error loading GLTF model:', error);
    }
  );

  return group;
}

function createGroup(element: MMLElement): THREE.Group {
  const attrs = element.attributes as unknown as GroupAttributes;

  return new THREE.Group();
}

// TODO: IMPLEMENT
function createAudio(element: MMLElement): THREE.Group {
  const attrs = element.attributes as unknown as AudioAttributes;

  const group = new THREE.Group();

  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color('blue'),
    transparent: true,
    opacity: 0.5,
  });
  const geometry = new THREE.SphereGeometry(0.3, 16, 16);
  const mesh = new THREE.Mesh(geometry, material);

  group.add(mesh);

  return group;
}

// TODO: IMPLEMENT
function createLabel(element: MMLElement): THREE.Group {
  const attrs = element.attributes as unknown as LabelAttributes;

  const group = new THREE.Group();

  const color = attrs.color || '#99DD99';
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    transparent: true,
    opacity: 0.8,
  });

  const width = attrs.width || 1;
  const height = attrs.height || 0.5;

  const geometry = new THREE.PlaneGeometry(width, height);
  const mesh = new THREE.Mesh(geometry, material);

  group.add(mesh);

  return group;
}

function createImage(element: MMLElement): THREE.Group {
  const attrs = element.attributes as unknown as ImageAttributes;
  const group = new THREE.Group();

  if (!attrs.src) {
    group.add(createPlaceholder(element));
    return group;
  }

  const textureLoader = new THREE.TextureLoader();

  const texture = textureLoader.load(
    attrs.src as string,
    undefined,
    undefined,
    (error) => {
      console.error('Error loading image texture:', error);
    }
  );

  /* texture.encoding = THREE.sRGBEncoding; */
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 8;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
  });

  const width = attrs.width || 1;
  const height = attrs.height || 1;
  const geometry = new THREE.PlaneGeometry(width, height);

  const mesh = new THREE.Mesh(geometry, material);

  group.add(mesh);

  return group;
}

function createVideo(element: MMLElement): THREE.Group {
  const attrs = element.attributes as unknown as VideoAttributes;
  const group = new THREE.Group();

  if (!attrs.src) {
    group.add(createPlaceholder(element));
    return group;
  }

  const video = document.createElement('video');
  video.src = attrs.src as string;
  video.crossOrigin = 'anonymous';
  video.loop = attrs.loop || false;
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;

  if (attrs['start-time'] !== undefined) {
    video.currentTime = attrs['start-time'] as number;
  }

  const texture = new THREE.VideoTexture(video);
  /* texture.encoding = THREE.sRGBEncoding; */
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 8;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
  });

  const width = attrs.width || 1;
  const height = attrs.height || 1;
  const geometry = new THREE.PlaneGeometry(width, height);

  const mesh = new THREE.Mesh(geometry, material);

  group.add(mesh);

  const backMaterial = new THREE.MeshBasicMaterial({
    color: new THREE.Color('black'),
    side: THREE.BackSide,
  });

  const backMesh = new THREE.Mesh(geometry, backMaterial);
  group.add(backMesh);

  video.play().catch((error) => {
    console.error('Error playing video:', error);
  });

  return group;
}
